import { useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  Card,
  Grid,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Button from 'src/components/md/MDButton';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Icon from '@mui/material/Icon';
import SupplierPill from '@/components/company/SupplierPill';

// Mock data for the supplier
const supplierData = {
  '19c70c35-65fb-4e30-a85e-de193085ebf9': {
    id: '19c70c35-65fb-4e30-a85e-de193085ebf9',
    name: 'Arlington',
    contactName: 'John Smith',
    email: 'john.smith@arlington.com',
    phone: '(555) 123-4567',
    address: '123 Industrial Way, Toolsville, CA 90210',
    amountOwed: 15750.0,
    amountPaid: 0,
    remainder: 15750.0,
    dueDate: '2025-06-15',
  },
  '59660244-e97a-4f55-bdbc-bda893497771': {
    id: '59660244-e97a-4f55-bdbc-bda893497771',
    name: 'Dals',
    contactName: 'Sarah Johnson',
    email: 'sarah.j@dals.com',
    phone: '(555) 987-6543',
    address: '456 Manufacturing Blvd, Factoryville, NY 10001',
    amountOwed: 12300.0,
    amountPaid: 12300.0,
    remainder: 0,
    dueDate: '2025-05-10',
  },
  'ec96dfd3-538c-48ee-b605-76705c8e18b7': {
    id: 'ec96dfd3-538c-48ee-b605-76705c8e18b7',
    name: 'Federal Signal',
    contactName: 'Michael Wong',
    email: 'm.wong@fedsignal.com',
    phone: '(555) 456-7890',
    address: '789 Signal Avenue, Lightville, IL 60611',
    amountOwed: 18450.0,
    amountPaid: 10000.0,
    remainder: 8450.0,
    dueDate: '2025-06-05',
  },
  '515d85d0-cb98-457b-af96-7e1db34f1ec1': {
    id: '515d85d0-cb98-457b-af96-7e1db34f1ec1',
    name: 'Hillman',
    contactName: 'Patricia Rodriguez',
    email: 'p.rodriguez@hillman.com',
    phone: '(555) 234-5678',
    address: '321 Hardware Lane, Mountview, TX 75001',
    amountOwed: 21750.0,
    amountPaid: 0,
    remainder: 21750.0,
    dueDate: '2025-06-25',
  },
  'b5148b50-67c9-4fd1-8d7a-fd97c5aa7365': {
    id: 'b5148b50-67c9-4fd1-8d7a-fd97c5aa7365',
    name: 'Rex Magnets',
    contactName: 'Robert Maxwell',
    email: 'r.maxwell@rexmagnets.com',
    phone: '(555) 876-5432',
    address: '987 Magnet Street, Polarville, CA 94203',
    amountOwed: 9500.0,
    amountPaid: 4750.0,
    remainder: 4750.0,
    dueDate: '2025-06-10',
  },
};

// Mock data for communications
interface Communication {
  id: string;
  date: string;
  type: 'email' | 'phone';
  subject?: string;
  notes?: string;
  response?: string;
}

interface ScheduledCommunication {
  id: string;
  scheduledDate: string;
  type: 'email' | 'phone';
  subject?: string;
  notes?: string;
  template?: string;
}

const communicationsData: Record<string, Communication[]> = {
  '19c70c35-65fb-4e30-a85e-de193085ebf9': [
    {
      id: '1',
      date: '2025-05-15',
      type: 'email',
      subject: 'Rebate Payment Reminder',
      notes: 'Sent reminder about upcoming rebate payment due on June 15',
      response: 'Will process payment next week',
    },
    {
      id: '2',
      date: '2025-05-18',
      type: 'phone',
      notes: 'Follow-up call regarding payment reminder email',
      response: 'Confirmed they received the email and are preparing payment',
    },
  ],
  '59660244-e97a-4f55-bdbc-bda893497771': [
    {
      id: '1',
      date: '2025-05-10',
      type: 'email',
      subject: 'Payment Confirmation',
      notes: 'Confirmation of received payment',
      response: 'Thank you for the confirmation',
    },
    {
      id: '2',
      date: '2025-05-08',
      type: 'phone',
      notes: 'Discussion about upcoming payment and process',
      response: 'Confirmed payment will be processed on May 10',
    },
  ],
  'ec96dfd3-538c-48ee-b605-76705c8e18b7': [
    {
      id: '1',
      date: '2025-05-05',
      type: 'phone',
      notes: 'Discussed partial payment and remainder schedule',
      response: 'Agreed to pay remainder by June 5',
    },
    {
      id: '2',
      date: '2025-04-30',
      type: 'email',
      subject: 'Q1 Rebate Calculation',
      notes: 'Sent detailed rebate calculation for Q1',
      response: 'Requested minor adjustment to calculation',
    },
  ],
  '515d85d0-cb98-457b-af96-7e1db34f1ec1': [
    {
      id: '1',
      date: '2025-05-18',
      type: 'email',
      subject: 'Rebate Payment Schedule',
      notes: 'Requested payment schedule for Q1 rebates',
      response: 'Will review and respond by May 25',
    },
    {
      id: '2',
      date: '2025-05-20',
      type: 'phone',
      notes: 'Follow-up call on payment schedule request',
      response: 'Still reviewing the rebate calculations, will respond soon',
    },
  ],
  'b5148b50-67c9-4fd1-8d7a-fd97c5aa7365': [
    {
      id: '1',
      date: '2025-05-12',
      type: 'phone',
      notes: 'Discussed partial payment and future payment plans',
      response: 'Will pay remainder by due date',
    },
    {
      id: '2',
      date: '2025-05-06',
      type: 'email',
      subject: 'Partial Payment Processed',
      notes: 'Confirmed receipt of partial payment',
      response: 'Confirmed remainder payment date',
    },
  ],
};

const scheduledCommunicationsData: Record<string, ScheduledCommunication[]> = {
  '19c70c35-65fb-4e30-a85e-de193085ebf9': [
    {
      id: '1',
      scheduledDate: '2025-06-10',
      type: 'email',
      subject: 'Final Payment Reminder',
      template: 'payment_reminder',
    },
    {
      id: '2',
      scheduledDate: '2025-06-11',
      type: 'phone',
      notes: 'Follow up on final payment reminder',
    },
  ],
  '59660244-e97a-4f55-bdbc-bda893497771': [
    {
      id: '1',
      scheduledDate: '2025-05-30',
      type: 'phone',
      notes: 'Check in about next quarter rebates',
    },
  ],
  'ec96dfd3-538c-48ee-b605-76705c8e18b7': [
    {
      id: '1',
      scheduledDate: '2025-06-03',
      type: 'email',
      subject: 'Reminder for Remaining Balance',
      template: 'balance_reminder',
    },
    {
      id: '2',
      scheduledDate: '2025-06-04',
      type: 'phone',
      notes: 'Follow up on balance reminder',
    },
  ],
  '515d85d0-cb98-457b-af96-7e1db34f1ec1': [
    {
      id: '1',
      scheduledDate: '2025-06-15',
      type: 'phone',
      notes: 'Call to discuss payment status',
    },
    {
      id: '2',
      scheduledDate: '2025-06-20',
      type: 'email',
      subject: 'Payment Reminder',
      template: 'payment_reminder',
    },
  ],
  'b5148b50-67c9-4fd1-8d7a-fd97c5aa7365': [
    {
      id: '1',
      scheduledDate: '2025-06-08',
      type: 'email',
      subject: 'Remainder Payment Reminder',
      template: 'payment_reminder',
    },
    {
      id: '2',
      scheduledDate: '2025-06-09',
      type: 'phone',
      notes: 'Follow up on payment reminder',
    },
  ],
};

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

// Format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

// Info row component for displaying supplier information
const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <MDBox display="flex" py={1} pr={2}>
    <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
      {label}:&nbsp;
    </MDTypography>
    <MDTypography variant="button" fontWeight="regular" color="text">
      {value}
    </MDTypography>
  </MDBox>
);

const SupplierCollectionsPage = () => {
  const { supplierId } = useParams({
    from: '/authenticated/rebates/supplier-collections/$supplierId',
  });
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [communicationTab, setCommunicationTab] = useState(0);
  const [, setSelectedCommunication] = useState<Communication | null>(null);

  // Find supplier data
  const supplier = supplierData[supplierId as keyof typeof supplierData];

  if (!supplier) {
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <MDTypography variant="h4" color="error">
            Supplier not found
          </MDTypography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate({
                // @ts-expect-error rebates is not in the route tree
                to: '/authenticated/rebates/reconciliation',
              })
            }
            sx={{ mt: 2 }}
          >
            Back to Rebates Reconciliation
          </Button>
        </MDBox>
      </DashboardLayout>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCommunicationTabChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setCommunicationTab(newValue);
  };

  const handleOpenCommunicationModal = (communication: Communication) => {
    setSelectedCommunication(communication);
    // In a real implementation, you would show a modal with details
    console.log('Open communication details:', communication);
  };

  // Get communications for this supplier
  const communications =
    communicationsData[supplierId as keyof typeof communicationsData] || [];
  const scheduledCommunications =
    scheduledCommunicationsData[
      supplierId as keyof typeof scheduledCommunicationsData
    ] || [];

  return (
    <DashboardLayout>
      <MDBox pt={2} pb={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <MDBox display="flex" alignItems="center">
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="h4" mb={0.5}>
                Rebate Collections
              </MDTypography>
              <SupplierPill supplierId={supplier.id} supplier={supplier} />
            </MDBox>
          </MDBox>
        </MDBox>

        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} md={4}>
            <Card sx={{ boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)' }}>
              <MDBox p={2}>
                <MDTypography variant="button" color="text.secondary">
                  Total Owed
                </MDTypography>
                <MDBox display="flex" alignItems="center" mt={1}>
                  <MDTypography variant="h5" fontWeight="medium">
                    {formatCurrency(supplier.amountOwed)}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)' }}>
              <MDBox p={2}>
                <MDTypography variant="button" color="text.secondary">
                  Amount Paid
                </MDTypography>
                <MDBox display="flex" alignItems="center" mt={1}>
                  <MDTypography
                    variant="h5"
                    fontWeight="medium"
                    color="success"
                  >
                    {formatCurrency(supplier.amountPaid)}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)' }}>
              <MDBox p={2}>
                <MDTypography variant="button" color="text.secondary">
                  Remaining
                </MDTypography>
                <MDBox display="flex" alignItems="center" mt={1}>
                  <MDTypography
                    variant="h5"
                    fontWeight="medium"
                    color={supplier.remainder > 0 ? 'warning' : 'success'}
                  >
                    {formatCurrency(supplier.remainder)}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Tabs for different views */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={2}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  textColor="primary"
                  indicatorColor="primary"
                >
                  <Tab label="Overview" />
                  <Tab label="Payment History" />
                  <Tab label="Rebate Details" />
                </Tabs>
              </MDBox>

              {/* Tab content */}
              <MDBox p={2}>
                {activeTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <MDBox p={3}>
                          <MDTypography variant="h6" fontWeight="medium" mb={3}>
                            Supplier Information
                          </MDTypography>
                          <InfoRow label="Supplier" value={supplier.name} />
                          <InfoRow
                            label="Contact Person"
                            value={supplier.contactName}
                          />
                          <InfoRow label="Email" value={supplier.email} />
                          <InfoRow label="Phone" value={supplier.phone} />
                          <InfoRow label="Address" value={supplier.address} />
                        </MDBox>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <MDBox p={3}>
                          <MDTypography variant="h6" fontWeight="medium" mb={3}>
                            Payment Details
                          </MDTypography>
                          <InfoRow
                            label="Due Date"
                            value={formatDate(supplier.dueDate)}
                          />
                          <InfoRow
                            label="Total Owed"
                            value={formatCurrency(supplier.amountOwed)}
                          />
                          <InfoRow
                            label="Amount Paid"
                            value={formatCurrency(supplier.amountPaid)}
                          />
                          <InfoRow
                            label="Remaining"
                            value={formatCurrency(supplier.remainder)}
                          />
                          {supplier.remainder === 0 && (
                            <MDBox mt={2}>
                              <MDTypography
                                variant="button"
                                color="success"
                                fontWeight="bold"
                              >
                                Fully Paid
                              </MDTypography>
                            </MDBox>
                          )}
                        </MDBox>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {activeTab === 1 && (
                  <Card>
                    <MDBox p={3}>
                      <MDTypography variant="h6" fontWeight="medium" mb={3}>
                        Payment History
                      </MDTypography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Payment Method</TableCell>
                              <TableCell>Reference Number</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {supplierId ===
                              '59660244-e97a-4f55-bdbc-bda893497771' && (
                              <TableRow>
                                <TableCell>May 10, 2025</TableCell>
                                <TableCell>{formatCurrency(12300.0)}</TableCell>
                                <TableCell>Wire Transfer</TableCell>
                                <TableCell>PMT-85472369</TableCell>
                              </TableRow>
                            )}
                            {supplierId ===
                              'ec96dfd3-538c-48ee-b605-76705c8e18b7' && (
                              <TableRow>
                                <TableCell>May 2, 2025</TableCell>
                                <TableCell>{formatCurrency(10000.0)}</TableCell>
                                <TableCell>ACH</TableCell>
                                <TableCell>PMT-76543210</TableCell>
                              </TableRow>
                            )}
                            {(supplierId ===
                              '19c70c35-65fb-4e30-a85e-de193085ebf9' ||
                              supplierId ===
                                '515d85d0-cb98-457b-af96-7e1db34f1ec1') && (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  <MDTypography variant="body2" color="text">
                                    No payment history available
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                            )}
                            {supplierId ===
                              'b5148b50-67c9-4fd1-8d7a-fd97c5aa7365' && (
                              <TableRow>
                                <TableCell>May 5, 2025</TableCell>
                                <TableCell>{formatCurrency(4750.0)}</TableCell>
                                <TableCell>Credit Card</TableCell>
                                <TableCell>PMT-92156487</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </MDBox>
                  </Card>
                )}

                {activeTab === 2 && (
                  <Card>
                    <MDBox p={3}>
                      <MDTypography variant="h6" fontWeight="medium" mb={3}>
                        Rebate Details
                      </MDTypography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Period</TableCell>
                              <TableCell>Sales Amount</TableCell>
                              <TableCell>Rebate Rate</TableCell>
                              <TableCell>Rebate Amount</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Q3 2023</TableCell>
                              <TableCell>{formatCurrency(350000.0)}</TableCell>
                              <TableCell>4.5%</TableCell>
                              <TableCell>
                                {formatCurrency(supplier.amountOwed)}
                              </TableCell>
                              <TableCell>
                                {supplier.remainder === 0 ? (
                                  <MDTypography
                                    variant="caption"
                                    color="success"
                                  >
                                    Paid
                                  </MDTypography>
                                ) : (
                                  <MDTypography
                                    variant="caption"
                                    color="warning"
                                  >
                                    Pending
                                  </MDTypography>
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </MDBox>
                  </Card>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Communication Tabs */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={3}>
                  Supplier Communications
                </MDTypography>
                <Tabs
                  value={communicationTab}
                  onChange={handleCommunicationTabChange}
                  textColor="primary"
                  indicatorColor="primary"
                  sx={{ mb: 3 }}
                >
                  <Tab
                    icon={<HistoryIcon fontSize="small" />}
                    iconPosition="start"
                    label="Communication History"
                  />
                  <Tab
                    icon={<ScheduleIcon fontSize="small" />}
                    iconPosition="start"
                    label="Scheduled Communications"
                  />
                </Tabs>

                {/* Communication History Tab */}
                {communicationTab === 0 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Details</TableCell>
                          <TableCell>Response</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {communications.length > 0 ? (
                          communications.map((comm) => (
                            <TableRow
                              key={comm.id}
                              onClick={() => handleOpenCommunicationModal(comm)}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'grey.100' },
                              }}
                            >
                              <TableCell>
                                <MDTypography variant="caption">
                                  {new Date(comm.date).toLocaleDateString()}
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDBox display="flex" alignItems="center">
                                  <Icon
                                    color={
                                      comm.type === 'email' ? 'info' : 'success'
                                    }
                                    sx={{ mr: 1 }}
                                  >
                                    {comm.type === 'email' ? 'email' : 'phone'}
                                  </Icon>
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="medium"
                                  >
                                    {comm.type === 'email'
                                      ? 'Email'
                                      : 'Phone Call'}
                                  </MDTypography>
                                </MDBox>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="caption">
                                  {comm.subject || comm.notes || '-'}
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="caption">
                                  {comm.response || '-'}
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <MDTypography variant="body2">
                                No communication history found
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Scheduled Communications Tab */}
                {communicationTab === 1 && (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Scheduled Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Details</TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {scheduledCommunications.length > 0 ? (
                            scheduledCommunications.map((comm) => (
                              <TableRow key={comm.id}>
                                <TableCell>
                                  <MDTypography variant="caption">
                                    {new Date(
                                      comm.scheduledDate,
                                    ).toLocaleDateString()}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDBox display="flex" alignItems="center">
                                    <Icon
                                      color={
                                        comm.type === 'email'
                                          ? 'info'
                                          : 'success'
                                      }
                                      sx={{ mr: 1 }}
                                    >
                                      {comm.type === 'email'
                                        ? 'email'
                                        : 'phone'}
                                    </Icon>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                    >
                                      {comm.type === 'email'
                                        ? 'Email'
                                        : 'Phone Call'}
                                    </MDTypography>
                                  </MDBox>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="caption">
                                    {comm.subject || comm.notes || '-'}
                                  </MDTypography>
                                  {comm.template && (
                                    <MDTypography
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                    >
                                      Template:{' '}
                                      {comm.template.replace('_', ' ')}
                                    </MDTypography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {/* <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                  >
                                    Edit
                                  </Button> */}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                <MDTypography variant="body2">
                                  No scheduled communications
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default SupplierCollectionsPage;
